// src/pages/profile/PerformanceDetails.jsx
import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { BADGES, getBadge } from "../../utils/Badges";

const PerformanceDetails = ({ pastAssestmentData }) => {

  const calculateTotalMarks = (marksPerSection) => {
    return Object.values(marksPerSection).reduce((sum, marks) => sum + marks, 0);
  };

  const getPercentage = (a) => {
    const total = calculateTotalMarks(a.marks_per_section);
    return total ? (a.total_score / total) * 100 : 0;
  };

  const summary = useMemo(() => {
    if (!pastAssestmentData.length) return null;

    const total = pastAssestmentData.length;

    const sortedByDate = [...pastAssestmentData].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );

    const lastTaken = sortedByDate[0];

    const best = [...pastAssestmentData].sort(
      (a, b) => getPercentage(b) - getPercentage(a)
    )[0];

    const avg =
      pastAssestmentData.reduce((sum, a) => sum + getPercentage(a), 0) / total;

    return { total, lastTaken, best, avg: avg.toFixed(1) };
  }, [pastAssestmentData]);

  const chartData = useMemo(() => {
    return pastAssestmentData
      .map((a) => ({
        date: new Date(a.created_at).toLocaleDateString(),
        percentage: Number(getPercentage(a).toFixed(1)),
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [pastAssestmentData]);

  const { badge, next } = getBadge(summary?.total);

  const progressInfo = useMemo(() => {
    if (!summary) return null;

    if (!next || !isFinite(next.threshold)) {
      return {
        done: summary.total,
        needed: summary.total,
        percent: 100,
        nextName: null,
      };
    }

    const done = summary.total;
    const needed = next.threshold;
    const percent = Math.min(100, (done / needed) * 100);

    return {
      done,
      needed,
      percent,
      remaining: Math.max(0, needed - done),
      nextName: next.name,
    };
  }, [summary, next]);


 const punchlines = useMemo(() => {
  if (!summary) return [];

  const { avg, total, best, lastTaken } = summary;

  let line1;
  if (avg >= 75) {
    line1 = ` You're on a roll! You've aced ${total} assessments with an average of ${avg}%. Keep the streak alive!`;
  } else if (avg >= 50) {
    line1 = ` Solid progress: ${total} assessments down, averaging ${avg}%. Let's push that into the green!`;
  } else {
    line1 = ` Every pro starts somewhere — ${total} attempts so far with an average of ${avg}%. Your breakthrough is next!`;
  }

  const line2 = `🏅 Your "${best.project_name}" score proves top marks are within reach. Ready to beat it?`;

  const lastPct = Number(getPercentage(lastTaken).toFixed(1));

  let line3;
  if (lastPct >= 75) {
    line3 = ` Latest test scored ${lastPct}% — outstanding! Carry that momentum forward.`;
  } else if (lastPct >= 50) {
    line3 = ` Last attempt landed ${lastPct}%. A small boost puts you in the top band — go for it!`;
  } else {
    line3 = `🔄 Your recent ${lastPct}% is a baseline to beat. Review, recharge, and smash the next assessment!`;
  }

  return [line1, line2, line3];
}, [summary]);

  return (
    <>
      
      {punchlines.map((txt, i) => (
        <p
          key={i}
          className={`max-w-3xl mx-auto px-4 py-2 rounded-lg text-center text-sm md:text-base ${
            i === 0
              ? "bg-gradient-to-l from-indigo-950 to-indigo-800 text-white shadow-md font-semibold"
              : i === 1
              ? "bg-indigo-50 text-indigo-700 border border-indigo-200"
              : "bg-green-50 text-green-700 border border-green-200"
          }`}
        >
          {txt}
        </p>
      ))}

      
      <style>{`
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    50% { transform: translateX(100%); }
    100% { transform: translateX(-100%); }
  }

  .shiny-bar::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 40%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent,
      rgba(255, 255, 255, 0.5),
      transparent
    );
    animation: shimmer 2s infinite;
  }

  .glossy::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(255,255,255,0.2);
    mix-blend-mode: overlay;
  }
`}</style>
      {progressInfo && (
        <div className="max-w-md mx-auto text-center space-y-2 mt-4">
          <div className="relative h-4 rounded-full overflow-hidden bg-indigo-100 border border-indigo-200 shadow-inner">
           <div style={{ width: `${progressInfo.percent}%` }}
            className="h-full relative bg-gradient-to-l from-indigo-950 to-indigo-800 transition-all duration-700 shiny-bar glossy shadow-[0_0_10px_rgba(99,102,241,0.6)]"
          />
          </div>

          <p className="text-xs text-gray-600">
            {progressInfo.percent === 100 ? (
              <>🏆 All badges unlocked!</>
            ) : (
              <>
                {progressInfo.needed - progressInfo.done} left to unlock{" "}
                <b>{progressInfo.nextName}</b>
              </>
            )}
          </p>
        </div>
      )}

      
      <div className="space-y-4 mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard title="Assessments" value={summary?.total ?? 0} />
          <SummaryCard title="Average %" value={`${summary?.avg ?? 0}%`} />
          <SummaryCard
            title="Last Taken"
            value={
              summary
                ? new Date(summary.lastTaken.created_at).toLocaleDateString()
                : "—"
            }
          />
          <SummaryCard
            title="Best"
            value={summary?.best?.project_name ?? "—"}
          />
        </div>

       
        <div className="bg-white border border-indigo-100 rounded-xl shadow-sm p-4">
          <div className="w-full h-56 md:h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e7ff" />
                <XAxis dataKey="date" tick={{ fill: "#6366f1", fontSize: 10 }} />
                <YAxis domain={[0, 100]} tick={{ fill: "#6366f1", fontSize: 10 }} />
                <Tooltip
                  contentStyle={{
                    borderRadius: "8px",
                    border: "1px solid #c7d2fe",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="percentage"
                  stroke="#36369f"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </>
  );
};

const SummaryCard = ({ title, value }) => (
  <div className="p-4 bg-white rounded-xl border border-indigo-100 shadow-sm text-center hover:shadow-md transition">
    <p className="text-xs uppercase tracking-wide text-indigo-400 font-semibold">
      {title}
    </p>

    <p className="text-xl font-bold text-indigo-700">{value}</p>

    <div className="h-1 w-8 mx-auto mt-2 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500"></div>
  </div>
);

export default PerformanceDetails;